import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { PageComponent, PageHeaderComponent } from '@softapps/core/pages';
import { backNavigation, title } from '@softline/application';
import { cancelableRxResource, IsoDate } from '@softline/core';
import {
  IconComponent,
  InputComponent,
  NumberInputComponent,
  SwitchComponent,
  TranslatePipe,
  translator,
} from '@softline/ui-core';
import { SelectComponent, SelectOptionDirective } from '@softline/ui-core/combo-box';
import { DateInputComponent } from '@softline/ui-core/date-input';
import { mapDocumentType } from '../../helper/document-form/map-document-type';
import { AgingBucket, DEFAULT_AGING_BUCKETS } from '../../sales.shared';
import { DocumentTypeService } from '../../services/document-type.service';
import { DistributionTypeService } from '../../services/distribution-type.service';
import { SalesConfigService } from '../../services/sales-config.service';

const DEFAULT_BUCKET_COLORS = [
  '#16a34a',
  '#eab308',
  '#f97316',
  '#ef4444',
  '#6366f1',
] as const;

function newBucket(index: number): AgingBucket {
  return {
    label: '',
    fromDays: 0,
    toDays: null,
    color: DEFAULT_BUCKET_COLORS[index % DEFAULT_BUCKET_COLORS.length],
  };
}

@Component({
  selector: 'soft-sales-config',
  standalone: true,
  imports: [
    PageComponent,
    PageHeaderComponent,
    TranslatePipe,
    DateInputComponent,
    InputComponent,
    NumberInputComponent,
    SelectComponent,
    SelectOptionDirective,
    FormField,
    CdkDropList,
    CdkDrag,
    CdkDragPlaceholder,
    IconComponent,
    SwitchComponent,
  ],
  templateUrl: './sales-config.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class SalesConfigComponent {
  private readonly configService = inject(SalesConfigService);
  private readonly documentTypeService = inject(DocumentTypeService);
  private readonly distributionTypeService = inject(DistributionTypeService);
  private readonly t = translator();

  private readonly distributionTypesResource = cancelableRxResource({
    params: () => ({}),
    stream: () => this.distributionTypeService.query(),
  });

  readonly data = this.configService.config;
  readonly configForm = form(this.data);

  readonly monthsBack = computed(() => this.data().monthsBack);
  readonly dateFrom = computed<IsoDate | string | null>(() => {
    const d = this.data();
    if (d.dateFrom) return d.dateFrom;
    const from = new Date();
    from.setMonth(from.getMonth() - (d.monthsBack ?? 3));
    return from.toISOString().slice(0, 10);
  });

  readonly documentTypeOptions = computed(() =>
    (this.documentTypeService.all() ?? []).map(mapDocumentType)
  );
  readonly orderedDocTypeOptions = computed(() => {
    const order = this.data().validDocumentTypes ?? [];
    return [...this.documentTypeOptions()].sort((a, b) => {
      const ai = order.indexOf(a.type);
      const bi = order.indexOf(b.type);
      return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
    });
  });
  readonly validDocTypeOptions = computed(() => {
    const valid = this.data().validDocumentTypes ?? [];
    return this.orderedDocTypeOptions().filter((o) => valid.includes(o.type));
  });
  readonly distributionTypes = computed(() =>
    (this.distributionTypesResource.value() ?? []).map((l) => ({
      id: String(l.id),
      name: l.bezeichnung,
    }))
  );

  constructor() {
    title(this.t('#SALES.CONFIG.TITLE'));
    backNavigation('/sales/overview');

    let initialized = false;
    effect(() => {
      const current = this.data();
      if (!initialized) {
        initialized = true;
        return;
      }
      untracked(() => this.configService.save(current));
    });
  }

  onMonthsChange(months: number | null): void {
    const m = months ?? 3;
    const from = new Date();
    from.setMonth(from.getMonth() - m);
    this.data.update((c) => ({
      ...c,
      monthsBack: m,
      dateFrom: from.toISOString().slice(0, 10),
    }));
  }

  onDateFromChange(dateFrom: IsoDate | string | null): void {
    if (!dateFrom) return;
    const from = new Date(dateFrom);
    const now = new Date();
    const months = Math.max(
      1,
      (now.getFullYear() - from.getFullYear()) * 12 + (now.getMonth() - from.getMonth())
    );
    this.data.update((c) => ({
      ...c,
      monthsBack: months,
      dateFrom,
    }));
  }

  onIndentPositionsChange(value: boolean): void {
    this.data.update((c) => ({ ...c, indentPositions: value }));
  }

  onDocTypeDrop(event: CdkDragDrop<string[]>): void {
    const ordered = this.orderedDocTypeOptions().map((o) => o.type);
    moveItemInArray(ordered, event.previousIndex, event.currentIndex);
    const valid = this.data().validDocumentTypes ?? [];
    const reordered = ordered.filter((t) => valid.includes(t));
    this.data.update((c) => ({ ...c, validDocumentTypes: reordered }));
  }

  isDocTypeSelected(type: string): boolean {
    return this.data().validDocumentTypes?.includes(type) ?? false;
  }

  /** Aging buckets editor — open/closed state lives in the URL-free local UI signal,
      while the actual buckets live in the persisted config. */
  readonly expandedAgingType = signal<string | null>(null);

  toggleAgingEditor(type: string): void {
    this.expandedAgingType.update((current) => (current === type ? null : type));
  }

  bucketsFor(type: string): readonly AgingBucket[] {
    return this.data().agingBuckets?.[type] ?? [];
  }

  /** Effective buckets used at runtime — overrides win, otherwise defaults. */
  effectiveBucketsFor(type: string): readonly AgingBucket[] {
    const override = this.data().agingBuckets?.[type];
    return override && override.length > 0 ? override : DEFAULT_AGING_BUCKETS;
  }

  hasOverride(type: string): boolean {
    return (this.data().agingBuckets?.[type]?.length ?? 0) > 0;
  }

  /** Copy the shared defaults into the per-type override so the user can
      tweak from a sensible starting point. */
  initFromDefaults(type: string): void {
    this.setBuckets(
      type,
      DEFAULT_AGING_BUCKETS.map((b) => ({ ...b }))
    );
  }

  resetToDefaults(type: string): void {
    this.setBuckets(type, []);
  }

  private setBuckets(type: string, buckets: AgingBucket[]): void {
    this.data.update((c) => {
      const next = { ...(c.agingBuckets ?? {}) };
      if (buckets.length === 0) {
        delete next[type];
      } else {
        next[type] = buckets;
      }
      return { ...c, agingBuckets: next };
    });
  }

  addBucket(type: string): void {
    const existing = [...this.bucketsFor(type)];
    existing.push(newBucket(existing.length));
    this.setBuckets(type, existing);
  }

  removeBucket(type: string, index: number): void {
    const next = this.bucketsFor(type).filter((_, i) => i !== index);
    this.setBuckets(type, [...next]);
  }

  updateBucket(type: string, index: number, patch: Partial<AgingBucket>): void {
    const next = this.bucketsFor(type).map((b, i) =>
      i === index ? { ...b, ...patch } : b
    );
    this.setBuckets(type, next);
  }

  onBucketToDaysChange(type: string, index: number, value: number | null): void {
    this.updateBucket(type, index, { toDays: value });
  }

  toggleDocType(type: string): void {
    const current = this.data().validDocumentTypes ?? [];
    const updated = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    this.data.update((c) => ({
      ...c,
      validDocumentTypes: updated,
      defaultDocumentType: updated.includes(c.defaultDocumentType ?? '')
        ? c.defaultDocumentType
        : null,
    }));
  }
}
